import { CopySettingsSubmenu } from '@/components/tiptap/shared/section-copy-settings-submenu';
import {
  SectionAiActionsList,
  SectionFormattingRow,
} from '@/components/tiptap/shared/section-selection-tools';
import { CopySettingsToggles } from '@/features/copySettings';
import { useIsSelectionAskHeidiEnabled } from '@/features/flags';
import { useIsImprovedCopyActionsEnabledForEditor } from '@/hooks/useMarkdownSectionalEditorFlags';
import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { type Node } from '@tiptap/pm/model';
import { type Editor } from '@tiptap/react';
import { MoreHorizontal, Settings, Trash2 } from 'lucide-react';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

export const SectionActionsDropdown = ({
  onDeleteSection,
  onOpenChange,
  ariaLabel,
  deleteSectionLabel,
  sectionNode,
  sectionId,
  editor,
  getSectionPos,
  emphasisDisabled,
}: {
  onDeleteSection: () => void;
  onOpenChange: (open: boolean) => void;
  ariaLabel: string;
  deleteSectionLabel: string;
  sectionNode: Node;
  sectionId: string;
  editor: Editor;
  /** Node-view position getter; enables the v5 selection tools. */
  getSectionPos?: () => number | undefined;
  /**
   * Disable the formatting row's bold/italic/underline buttons — the "Bold &
   * italics" copy setting is off and those marks are wiped from the note
   * display. Resolved by the hosting section node view, which
   * knows whether it sits in the note editor.
   */
  emphasisDisabled?: boolean;
}) => {
  // With improved copy actions on, section copy honours the user's global
  // copy settings, so the per-section "Copy settings" submenu is removed —
  // the flag-off cohort keeps the legacy per-device submenu.
  const isImprovedCopyEnabled = useIsImprovedCopyActionsEnabledForEditor();
  const isSelectionAskHeidiEnabled = useIsSelectionAskHeidiEnabled();
  const showSelectionTools = isSelectionAskHeidiEnabled && !!getSectionPos;
  const [isOpen, setIsOpen] = useState(false);

  /**
   * The section's body range (everything under the heading). The selection
   * tools operate on this range: rewriting the heading together with the body
   * would flatten the section structure on Accept.
   */
  const getSectionBodyRange = useCallback((): { from: number; to: number } | null => {
    const pos = getSectionPos?.();
    if (typeof pos !== 'number' || sectionNode.childCount === 0) return null;
    const heading = sectionNode.child(0);
    const from = pos + 1 + heading.nodeSize;
    const to = pos + sectionNode.nodeSize - 1;
    if (to <= from) return null;
    const text = editor.state.doc.textBetween(from, to, '\n').trim();
    return text ? { from, to } : null;
  }, [editor, getSectionPos, sectionNode]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setIsOpen(nextOpen);
      onOpenChange(nextOpen);
    },
    [onOpenChange]
  );

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    onOpenChange(false);
  }, [onOpenChange]);

  // Gate the selection tools on there being an actual body range to target;
  // computed once so the formatting row (top) and AI actions (bottom) stay in
  // sync without recomputing the range for each.
  const hasSelectionTools = showSelectionTools && getSectionBodyRange() !== null;

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          onMouseDown={(event) => event.preventDefault()}
          size="xs"
          variant="ghost"
          iconOnly
          type="button"
          aria-label={ariaLabel}
          data-testid="section-actions-trigger"
        >
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={hasSelectionTools ? 'p-0' : undefined}>
        {hasSelectionTools && (
          <>
            <SectionFormattingRow
              editor={editor}
              getTargetRange={getSectionBodyRange}
              emphasisDisabled={emphasisDisabled}
            />
            <DropdownMenuSeparator />
          </>
        )}
        {!isImprovedCopyEnabled && (
          <CopySettingsSubmenu
            sectionNode={sectionNode}
            sectionId={sectionId}
            editor={editor}
          />
        )}
        {isImprovedCopyEnabled && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger
              data-testid="section-settings-trigger"
              className={hasSelectionTools ? 'mx-2 my-1' : undefined}
            >
              <Settings className="text-text-secondary" />
              <FormattedMessage
                defaultMessage="Copy settings"
                id="y8UtDSYUqr"
                description="Section kebab menu item that opens the copy-settings toggles"
              />
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="min-w-64">
              <DropdownMenuLabel className="border-b border-border text-text-secondary">
                <FormattedMessage defaultMessage="Copy settings" id="DLkMVV4+xt" />
              </DropdownMenuLabel>
              <div className="flex flex-col gap-0.5 pt-1">
                <CopySettingsToggles />
              </div>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
        )}
        <DropdownMenuItem
          onMouseDown={(event) => event.preventDefault()}
          onSelect={onDeleteSection}
          variant="destructive"
          data-testid="delete-section-menu-item"
          // Match the inset of the selection-tool rows above/below (their
          // section wrapper adds p-2 that bare menu items don't have).
          className={hasSelectionTools ? 'mx-2 my-1' : undefined}
        >
          <Trash2 />
          {deleteSectionLabel}
        </DropdownMenuItem>
        {hasSelectionTools && (
          <>
            <DropdownMenuSeparator />
            <SectionAiActionsList
              editor={editor}
              getTargetRange={getSectionBodyRange}
              onAiActionStart={closeMenu}
            />
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
